import { useParams, Link, useLocation } from "react-router-dom";
import { UrlPath } from "utility/routes";
import useAccountData from "hooks/useAccountData";
import { useEffect, useState } from "react";
import { Company } from "models/Company";
import LoadingScreen from "components/LoadingScreen";
import BreadcrumbCompany from "./BreadcrumbCompany";
import CompanyConsent from "./Edit/CompanyConsent";
import { PhoneNumbersList, DetailsList, IndustriesList, LinksList, OpeningHoursList } from "./Edit/InfoList";
import { Seo } from "components/Seo";
import LogoPromo from "./Webshop/Promo/LogoPromo";
import { webShopService } from "services/Services";
import { PendingOrderResponse } from "models/WebShop";
import SessionKiller from "components/SessionKiller";
import WebshopPromo from "./Webshop/Promo/WebshopPromo";
import ProductList from "components/ProductList";
import InfoTextPromo from "./Webshop/Promo/InfoTextPromo";
import BulletpointsPromo from "./Webshop/Promo/BulletpointsPromo";
import ExtraLinksPromo from "./Webshop/Promo/ExtraLinksPromo";
import SearchWordsPromo from "./Webshop/Promo/SearchWordsPromo";
import Blog from "components/Blog";
import LogoDiscountPromo from "./Webshop/DiscountPromo/LogoDiscountPromo";

export default function EditCompanyInfoList() {
  let params = useParams();
  const { getCurrentCompany, loadingCompanies } = useAccountData();
  const [data, setData] = useState<Company | null>()
  const [hasContact, setHasContact] = useState<boolean>(false);
  const [order,  setOrder] = useState<PendingOrderResponse | undefined>(undefined)

  const location = useLocation();

  useEffect(() => {
    if ((data?.phoneNumbers !== undefined && data?.phoneNumbers.length > 0)
       || (data?.emails !== undefined && data?.emails.length > 0 && data?.emails[0].value !== "" )
       || (data?.urls !== undefined && data?.urls.length > 0 && data?.urls[0].value) 
       || (data?.socialMediaUrls !== undefined && data?.socialMediaUrls.length > 0)){
      setHasContact(true);
    }
  }
  ,[data?.phoneNumbers, data?.emails, data?.urls, data?.socialMediaUrls])

  const turncatHandler = () => {
    document.getElementsByClassName('cc-table-trunc')[0].classList.remove('cc-table-trunc');
  }

  useEffect(() => {
    async function fetchData() {
      if (loadingCompanies) {
        const response = await getCurrentCompany(params.id as string);
        const checkOrder = await webShopService.hasPendingOrder(params.id as string, response?.companyCode as string);
        setData(response);
        setOrder(checkOrder);
        
          window.dataLayer.push({
        event: 'business_event',
        page: location.pathname + location.search,
        is_advertiser: response?.hasProducts
      });
      }
    }
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCurrentCompany, params.id, loadingCompanies]);

  const updateCompany = (company: Company) => {
    setData(company);
  }

  return (
    <><Seo title="Endre opplysninger" />
      {data === null || order === undefined ? (<LoadingScreen />) : !data?.isConsentGiven ? (<CompanyConsent company={data} updateCompany={updateCompany}></CompanyConsent>) : (<>
        <BreadcrumbCompany />
        <SessionKiller></SessionKiller>
        {hasContact ? (<h1 className="h3">Endre opplysninger</h1>) : (<><h1 className="h3">Legg til bedriftens kontaktinformasjon</h1><p>For å endre eller legge til opplysninger må du først legge til minst en type kontaktinformasjon for denne bedriften.</p> </>)}
        {data.manualVerificationStatus === 1  ? <div className="alert alert-danger" role="alert"> Lagrede endringer venter på 1881s godkjenning </div> : null}
        {hasContact && data.companyCode !== null && <WebshopPromo/>}
        {hasContact && data.companyCode !== null && <LogoDiscountPromo/>}
        {hasContact && (<><div className="card mt-4">
          <div className="card-header"><h2>Markedsnavn og adresse</h2></div>
          <div className="card-body">
            <table className="table cc-table-2-col mb-4">
              <DetailsList company={data} />
            </table>
            <p className="mb-0">
              <Link
                to={`${UrlPath.Account.Company}/${params.id}/${UrlPath.Account.Actions.Details}`}
                className="btn btn-primary text-white"
              >
                Endre markedsnavn og adresser
              </Link>
            </p>
          </div>
        </div>
        </>)}

        <div className="card mt-4">
          <div className="card-header"><h2>Telefonnummer</h2></div>
          <div className="card-body">
            <table className="table cc-table-2-col cc-table-trunc mb-4">
              <PhoneNumbersList phoneNumbers={data?.phoneNumbers} mainContactPointId={data?.mainContactPointId} />
            </table>
            <p className="mb-4 cc-table-trunc-expand"><span className="link-primary" onClick={turncatHandler}>Vis alle…</span></p>
            <p className="mb-0">
              <Link
                to={`${UrlPath.Account.Company}/${params.id}/${UrlPath.Account.Actions.PhoneNumbers}`}
                className="btn btn-primary text-white"
              >
                {hasContact ? 'Endre/legg til telefonnummer' : 'Legg til telefonnummer'}
              </Link>
            </p>
          </div>
        </div>
        <div className="card mt-4">
          <div className="card-header"><h2>Lenker</h2></div>
          <div className="card-body">
            <table className="table cc-table-2-col mb-4">
              <LinksList urls={data?.urls} emails={data?.emails} soMe={data?.socialMediaUrls} />
            </table>
            <p className="mb-0"><Link to={`${UrlPath.Account.Company}/${params.id}/${UrlPath.Account.Actions.Links}`} className="btn btn-primary text-white">{hasContact ? 'Endre/legg til lenker' : 'Legg til lenker'}</Link></p>
          </div>
        </div>
        { hasContact && (<><div className="card mt-4">
          <div className="card-header"><h2>Bransjer</h2></div>
          <div className="card-body">
            <table className="table cc-table-2-col mb-4">
              <IndustriesList industries={data?.industries} mainIndustryCode={data?.mainIndustryCode} />
            </table>
            <p className="mb-0"><Link to={`${UrlPath.Account.Company}/${params.id}/${UrlPath.Account.Actions.Industries}`} className="btn btn-primary text-white">Endre bransje{ data?.industries.length > 1 ? 'r' : '' }</Link></p>
          </div>
        </div>
        <div className="card mt-4">
          <div className="card-header"><h2>Åpningstider</h2></div>
          <div className="card-body">
            <table className="table cc-table-2-col mb-4">
              <OpeningHoursList openingHours={data?.openingHours} />
            </table>
            <p className="mb-0"><Link to={`${UrlPath.Account.Company}/${params.id}/${UrlPath.Account.Actions.OpeningHours}`} className="btn btn-primary text-white">{data?.openingHours?.showOpeningHours ? "Endre" : "Legg til"} åpningstider</Link></p>
          </div>
        </div>
        <ExtraLinksPromo/>
        <LogoPromo/>
        <InfoTextPromo/>
        <BulletpointsPromo/>
        <SearchWordsPromo/>
        { data.hasProducts &&
        <div className="card mt-4">
    <div className="card-header">
      <h2 className="fs-5">Endre andre kjøpte produkter</h2>
    </div>
    <div className="card-body">
      <p>Tjenesten Min bedrift er under utvikling. Ikke alle
        produkter kan endres i tjenesten ennå.  Kontakt <a href="https://kundeservice.1881.no/hc/no/categories/29337271299741-Min-Bedrift">kundeservice</a> for å endre andre produkter.</p>
      <p className="mb-0"><Link to={`${UrlPath.Account.Company}/${params.id}/${UrlPath.Account.Actions.Products}`}><b>Se oversikt produkter</b></Link></p>
    </div>
  </div>}

        </>)
}
        <p className="mt-4 mb-0">
          <Link to={UrlPath.MyAccount.Home} className="btn btn-outline-primary">
            <i className="bi bi-arrow-left"></i> Tilbake
          </Link>
        </p>
        <Blog id={"min-bedrift-promo-for-dm1881no"}/>

      </>)}
    </>
  );
};